import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects/hooks/projectHooks';
import { buildSidebarSections } from '@/layouts/sidebar/sidebarNavBuilders';
import { useNotifications } from '@/providers/notificationHooks';
import { layoutSizes } from '@/theme/layoutSizes';
import { fontFamilies, iconSizes, s, typographyTokens } from '@/theme/tokens';
import { r } from '@/theme/tokens/radii';
import { Box, Chip, CircularProgress, Inline, Stack, Text } from '@/ui';
import { SidebarNav } from '@/ui/composites/SidebarNav';
import { AppShell } from '@/ui/layout/AppShell';
import { layoutModeTokens, useLayoutMode } from '@/ui/layouts/layoutMode';
import { Button } from '@/ui/primitives';
import type { AuthSession } from '@/auth/types';
import type { NavItem } from '@/components/sidebarTypes';
import type { FeatureFlagResolveResult } from '@/shared/data/featureFlags';
import type { OrganisationDto, OrganisationEntitlementsDto, ProjectDto } from '@flowqa/shared';
import type { ReactElement, ReactNode } from 'react';

const TopBarActions = lazy(async () => {
  const module = await import('@/components/TopBarActions');
  return { default: module.TopBarActions };
});

type AuthenticatedLayoutProps = {
  session: AuthSession | null;
  isAdmin: boolean;
  isSuperuser: boolean;
  navItems: NavItem[];
  sidebarFeatureFlags?: FeatureFlagResolveResult['flags'] | null;
  addOns?: OrganisationEntitlementsDto['addOns'] | null;
  hideSidebar?: boolean;
  showSidebarWhenSignedOut?: boolean;
  forceShowSignOut?: boolean;
  restrictTopBarToSignOut?: boolean;
  disableTopBarSignOut?: boolean;
  activePath: string;
  projects: Array<Pick<ProjectDto, 'id' | 'name' | 'slug' | 'badgeChars' | 'badgeColor'>>;
  allProjects: Array<Pick<ProjectDto, 'id' | 'name' | 'slug' | 'badgeChars' | 'badgeColor'>>;
  organisations: Array<Pick<OrganisationDto, 'id' | 'name' | 'slug'>>;
  canCreateProject?: boolean;
  planName?: string | null;
  lockProjectSelector?: boolean;
  onNavigate: (path: string) => void;
  onAddProject: () => void;
  onAddOrganisation: () => void;
  onSignOut: () => void;
  children?: ReactNode;
};

export const AuthenticatedLayout = ({
  session,
  isAdmin,
  isSuperuser,
  navItems,
  sidebarFeatureFlags,
  addOns = null,
  hideSidebar = false,
  showSidebarWhenSignedOut = false,
  forceShowSignOut = false,
  restrictTopBarToSignOut = false,
  disableTopBarSignOut = false,
  activePath,
  projects,
  allProjects,
  organisations,
  canCreateProject = true,
  planName = null,
  lockProjectSelector = false,
  onNavigate,
  onAddProject,
  onAddOrganisation,
  onSignOut,
  children,
}: AuthenticatedLayoutProps): ReactElement => {
  const { selectedOrganisationId, setSelectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const { notify } = useNotifications();
  const layoutMode = useLayoutMode();
  const useFixedTopbarOrgWidth = layoutMode === layoutModeTokens.wide;
  const resolvedProjectOptions = useMemo(
    () => (projects.length > 0 ? projects : allProjects),
    [allProjects, projects],
  );
  const projectLabel = useMemo(() => {
    if (selectedProjectId) {
      const selectedProject = resolvedProjectOptions.find(
        (project) => project.id === selectedProjectId,
      );
      return selectedProject?.name ?? 'All Projects';
    }
    if (lockProjectSelector && resolvedProjectOptions.length === 1) {
      return resolvedProjectOptions[0]?.name ?? 'All Projects';
    }
    return 'All Projects';
  }, [lockProjectSelector, resolvedProjectOptions, selectedProjectId]);
  const selectedSidebarProject = useMemo(() => {
    if (selectedProjectId) {
      return resolvedProjectOptions.find((project) => project.id === selectedProjectId) ?? null;
    }
    if (lockProjectSelector && resolvedProjectOptions.length === 1) {
      return resolvedProjectOptions[0] ?? null;
    }
    return null;
  }, [lockProjectSelector, resolvedProjectOptions, selectedProjectId]);
  const selectedOrganisationName =
    selectedOrganisationId === null
      ? (session?.orgName ?? null)
      : (organisations.find((org) => org.id === selectedOrganisationId)?.name ??
        (session?.orgId === selectedOrganisationId ? (session.orgName ?? null) : null));
  const showTopbarOrganisationName = activePath !== '/onboarding';
  const topbarOrganisationLabel = selectedOrganisationName ?? 'Organisation';
  const showTopbarOrganisationSeparator = Boolean(selectedOrganisationName);

  const shouldRenderSidebar = !hideSidebar && (Boolean(session) || showSidebarWhenSignedOut);
  const sidebarContent = useMemo(
    () =>
      buildSidebarSections({
        session,
        navItems,
        activePath,
        onNavigate,
        featureFlags: sidebarFeatureFlags,
        addOns,
        isAdmin,
        isSuperuser,
        planName,
        selectedOrganisationId,
        selectedProjectId,
        setSelectedOrganisationId,
        setSelectedProjectId,
        notify,
        projectsOptions: resolvedProjectOptions,
        allProjectsOptions: allProjects,
        organisationsOptions: organisations,
        canCreateProject,
        lockProjectSelector,
        projectLabel,
        projectBadge: selectedSidebarProject
          ? {
              chars: selectedSidebarProject.badgeChars,
              color: selectedSidebarProject.badgeColor,
            }
          : null,
        onAddProject,
        onAddOrganisation,
      }),
    [
      activePath,
      canCreateProject,
      isAdmin,
      isSuperuser,
      navItems,
      notify,
      onAddOrganisation,
      onAddProject,
      onNavigate,
      allProjects,
      addOns,
      organisations,
      planName,
      resolvedProjectOptions,
      selectedOrganisationId,
      selectedProjectId,
      setSelectedOrganisationId,
      setSelectedProjectId,
      lockProjectSelector,
      projectLabel,
      selectedSidebarProject,
      session,
      sidebarFeatureFlags,
    ],
  );

  const sidebar = shouldRenderSidebar ? (
    <SidebarNav sections={sidebarContent.sections} footerItems={sidebarContent.footerItems} />
  ) : null;
  const restrictTopBarNavigation = activePath === '/onboarding' || restrictTopBarToSignOut;
  const shouldShowFallbackSignOut = forceShowSignOut || !session || restrictTopBarToSignOut;
  const [showDeferredTopBarActions, setShowDeferredTopBarActions] = useState(false);
  const [renderedPath, setRenderedPath] = useState(activePath);

  useEffect(() => {
    if (restrictTopBarNavigation || shouldShowFallbackSignOut) {
      setShowDeferredTopBarActions(false);
      return;
    }
    if (typeof window === 'undefined') {
      setShowDeferredTopBarActions(true);
      return;
    }
    let rafId: number | null = null;
    let timeoutId: number | null = null;
    const scheduleLoad = (): void => {
      timeoutId = window.setTimeout(() => {
        setShowDeferredTopBarActions(true);
      }, 200);
    };
    if (typeof window.requestAnimationFrame === 'function') {
      rafId = window.requestAnimationFrame(scheduleLoad);
    } else {
      scheduleLoad();
    }
    return () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [restrictTopBarNavigation, shouldShowFallbackSignOut]);
  useEffect(() => {
    if (renderedPath === activePath) {
      return;
    }
    if (typeof window === 'undefined') {
      setRenderedPath(activePath);
      return;
    }
    const timeoutId = window.setTimeout(() => {
      setRenderedPath(activePath);
    }, 0);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [activePath, renderedPath]);
  const topBarActionsFallback = (
    <Inline gap="sm" align="center" sx={{ width: 'auto', pr: s('sm') }}>
      <Box
        sx={{
          width: `calc(${s('2xl')} + ${s('md')})`,
          height: `calc(${s('2xl')} + ${s('md')})`,
          borderRadius: r('pill'),
        }}
      />
    </Inline>
  );
  const actions =
    restrictTopBarNavigation || shouldShowFallbackSignOut ? (
      <Inline gap="md" align="center" sx={{ width: 'auto' }}>
        {disableTopBarSignOut ? null : (
          <Button variant="text" onClick={onSignOut}>
            Sign Out
          </Button>
        )}
      </Inline>
    ) : showDeferredTopBarActions ? (
      <Suspense fallback={topBarActionsFallback}>
        <TopBarActions session={session} actionsGap="sm" />
      </Suspense>
    ) : (
      topBarActionsFallback
    );
  const isRouteTransitionPending = renderedPath !== activePath;
  const routeTransitionFallback = (
    <Box
      sx={{
        minHeight: '45vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Stack gap="md" align="center">
        <Text role="body" color="textSecondary" align="center">
          Loading page...
        </Text>
        <CircularProgress size={iconSizes.lg} />
      </Stack>
    </Box>
  );

  const titleCore = (
    <>
      <Box
        component="img"
        src="/logo.png"
        alt="FlowQA"
        sx={{ height: '36px', width: '36px', display: 'block' }}
      />
      <Text
        role="label"
        component="span"
        sx={{ fontFamily: fontFamilies.mono, fontSize: 24, lineHeight: 1 }}
      >
        [FLOWQA]
      </Text>
      {session ? (
        <Chip label="Beta" size="xs" tone="pink" transform="uppercase" sx={{ ml: s('xs') }} />
      ) : null}
    </>
  );
  const titleContent = (
    <Box
      sx={{
        color: 'inherit',
        textDecoration: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        gap: s('sm'),
        paddingTop: s('md'),
        paddingBottom: s('none'),
      }}
    >
      {restrictTopBarNavigation ? (
        titleCore
      ) : (
        <Box
          component={RouterLink}
          to="/execution-runs"
          sx={{ textDecoration: 'none', color: 'inherit' }}
        >
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: s('sm') }}>{titleCore}</Box>
        </Box>
      )}
      {showTopbarOrganisationName ? (
        <Box
          component="span"
          sx={{
            display: { xs: 'none', sm: 'inline-flex' },
            alignItems: 'center',
            gap: s('md'),
            marginLeft: s('sm'),
            minWidth: useFixedTopbarOrgWidth ? `calc(${s('5xl')} + ${s('3xl')})` : 'auto',
          }}
        >
          {showTopbarOrganisationSeparator ? (
            <Text
              role="body"
              component="span"
              sx={{
                fontSize: 19,
                lineHeight: 1,
                display: 'inline-flex',
                alignItems: 'center',
                marginBottom: s('md'),
              }}
            >
              ·
            </Text>
          ) : null}
          <Text
            role="body"
            component="span"
            sx={{
              fontSize: 19,
              fontWeight: typographyTokens.label.fontWeight,
              lineHeight: 1,
              display: 'inline-flex',
              alignItems: 'center',
              marginBottom: s('md'),
              whiteSpace: 'nowrap',
              maxWidth: useFixedTopbarOrgWidth ? `calc(${s('5xl')} + ${s('3xl')})` : 'none',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              visibility: selectedOrganisationName ? 'visible' : 'hidden',
            }}
          >
            {topbarOrganisationLabel}
          </Text>
        </Box>
      ) : null}
    </Box>
  );

  return (
    <AppShell title={titleContent} actions={actions} sidebar={sidebar} showDefaultActions={false}>
      <Box
        key={isRouteTransitionPending ? `${activePath}:transition` : activePath}
        sx={{ width: '100%', maxWidth: layoutSizes.content.maxWidth, mx: 'auto', minWidth: 0 }}
      >
        {isRouteTransitionPending ? routeTransitionFallback : children}
      </Box>
    </AppShell>
  );
};
